import type { CSSProperties, FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowUpRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad2, shapeKey } from '@/lib/format'
import { FigLabel } from '@/components/generals/fig-label'
import { PointCloud } from '@/components/generals/point-cloud'
import { Reveal } from '@/components/generals/reveal'
import { TextLink } from '@/components/generals/text-link'
import { HoverThumbnail } from '@/components/projects/hover-thumbnail'

interface SelectedWorkProps {
    projects: Array<ProjectSummary>
}

/**
 * Home section 09: three project rows with a trailing point-cloud thumbnail on hover and an
 * inline tap-to-expand preview on touch screens.
 */
export const SelectedWork: FC<SelectedWorkProps> = ({ projects }): ReactNode => {
    const [hovered, setHovered] = useState<ProjectSummary | null>(null)
    const [expanded, setExpanded] = useState<string | null>(null)
    const rows = projects.slice(0, 3)

    if (rows.length === 0) return null

    return (
        <section id='selected-work' aria-labelledby='selected-work-title' className='relative border-t border-hairline'>
            <Reveal className='flex flex-wrap items-end justify-between gap-8 px-gutter pb-12 pt-20 desk:pb-16 desk:pt-28'>
                <div>
                    <FigLabel fig='09' title='Selected work' detail='Still reporting' note='Hover a row for its live scan' />

                    <h2 id='selected-work-title' className='display-lg mt-10'>
                        <span className='reveal-line'>
                            <span className='reveal-inner text-ink'>
                                Three buildings,
                            </span>
                        </span>

                        <span className='reveal-line'>
                            <span className='reveal-inner text-dim' style={{ '--delay': '90ms' } as CSSProperties}>
                                three climates.
                            </span>
                        </span>
                    </h2>
                </div>

                <div className='reveal-fade'>
                    <TextLink href='/projects'>
                        All projects
                    </TextLink>
                </div>
            </Reveal>

            <ul className='group/list border-t border-hairline' onPointerLeave={() => setHovered(null)}>
                {rows.map((project, index) => {
                    const isOpen = expanded === project.id

                    return (
                        <li
                            key={project.id}
                            className={cn(
                                'group/row relative border-b border-hairline transition-opacity duration-500 ease-instrument',
                                hovered && hovered.id !== project.id && 'desk:opacity-35'
                            )}
                        >
                            <span
                                className={cn(
                                    'absolute inset-y-0 left-0 w-[3px] origin-top bg-signal transition-transform duration-500 ease-instrument',
                                    hovered?.id === project.id || isOpen ? 'scale-y-100' : 'scale-y-0'
                                )}
                                aria-hidden='true'
                            />

                            <div className='flex items-stretch'>
                                <Link
                                    href={`/projects/${project.slug}`}
                                    onPointerEnter={event => {
                                        if (event.pointerType === 'mouse') setHovered(project)
                                    }}
                                    onFocus={() => setHovered(project)}
                                    onBlur={() => setHovered(null)}
                                    className='grid min-w-0 flex-1 grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-x-4 gap-y-2 py-7 pl-gutter pr-4 desk:grid-cols-[4rem_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] desk:gap-x-8 desk:py-9 desk:pr-gutter'
                                >
                                    <span className='hud self-start pt-2 text-mute desk:self-center desk:pt-0'>
                                        {pad2(index + 1)}
                                    </span>

                                    <span className='min-w-0'>
                                        <span
                                            className='block truncate font-heading text-[clamp(30px,3.6vw,56px)] font-medium leading-none tracking-[-0.03em] text-ink transition-transform duration-500 ease-instrument desk:group-hover/row:translate-x-2'
                                            data-mdlfy-cms={`projects:${project.id}:name`}
                                        >
                                            {project.name}
                                        </span>

                                        <span className='hud mt-3 flex gap-3 text-mute desk:hidden'>
                                            <span data-mdlfy-cms={`projects:${project.id}:city`}>
                                                {project.city}
                                            </span>

                                            <span>
                                                {project.year}
                                            </span>
                                        </span>
                                    </span>

                                    <span className='hud hidden gap-3 text-mute desk:flex'>
                                        <span className='text-ink' data-mdlfy-cms={`projects:${project.id}:city`}>
                                            {project.city}
                                        </span>

                                        <span>
                                            {project.year}
                                        </span>
                                    </span>

                                    <span className='hud hidden text-mute desk:block' data-mdlfy-cms={`projects:${project.id}:structural_system`}>
                                        {project.structuralSystem}
                                    </span>

                                    <ArrowUpRight className='hidden size-5 text-ink transition-transform duration-500 ease-instrument group-hover/row:-translate-y-1 group-hover/row:translate-x-1 desk:block' strokeWidth={1.25} aria-hidden='true' />
                                </Link>

                                <button
                                    type='button'
                                    aria-expanded={isOpen}
                                    aria-controls={`preview-${project.id}`}
                                    onClick={() => setExpanded(isOpen ? null : project.id)}
                                    className='flex w-14 shrink-0 items-center justify-center border-l border-hairline text-ink desk:hidden'
                                >
                                    <Plus className={cn('size-4 transition-transform duration-300', isOpen && 'rotate-45')} strokeWidth={1.5} aria-hidden='true' />

                                    <span className='sr-only'>
                                        {isOpen ? 'Hide preview of ' : 'Show preview of '}
                                        {project.name}
                                    </span>
                                </button>
                            </div>

                            <div id={`preview-${project.id}`} hidden={!isOpen} className='border-t border-hairline desk:hidden'>
                                {isOpen && (
                                    <PointCloud
                                        shape={shapeKey(project.shape, project.slug)}
                                        label={`Point cloud of ${project.name}`}
                                        budget='thumb'
                                        rotate='always'
                                        rotateSpeed={8}
                                        effects
                                        frame
                                        interactive
                                        className='h-64 w-full'
                                    />
                                )}
                            </div>
                        </li>
                    )
                })}
            </ul>

            <HoverThumbnail project={hovered} />
        </section>
    )
}
